
import { Component, Vue, Prop, Watch } from 'vue-property-decorator';
import Chart from 'chart.js/auto';
import { ChartConfiguration } from 'chart.js';

import { sharedState } from '../../../../framework/state';

import { CostReport } from '../../../../../common/application/model/report/CostReport';
import {
    doughnutChartOptions,
    expenseTypeColors,
    fakeCostReportData,
    getTitleOptions,
} from '../../../service/report_service';
import { formatCurrency, sortAlphabetically } from '../../../service/helper_service';

@Component
export default class CostTypeChart extends Vue {
    @Prop(Array) readonly rows!: CostReport[];
    @Prop(Boolean) readonly showWithVat!: boolean;

    shared = sharedState;

    local = {
        rows: [] as CostReport[],
        costTypeChart: undefined as Chart | undefined,
        sum: 0,
    };

    mounted() {
        this.local.rows = this.rows;

        if (!this.rows.length) {
            this.generateFakeData();
        }

        setTimeout(() => {
            this.addCostTypeChart();
        }, 200);
    }

    @Watch('rows')
    rowsChanged(rows: CostReport[]) {
        this.local.rows = [...rows].sort(sortAlphabetically('invoiceRowTypeLabel'));

        if (!this.rows.length) {
            this.generateFakeData();
        }

        this.updateChart();
    }

    @Watch('showWithVat')
    showWithVatChange() {
        this.updateChart();
    }

    private addCostTypeChart() {
        this.countTotalSum();
        const canvasContext = (document.getElementById('cost-type-chart') as HTMLCanvasElement)!!.getContext('2d')!!;
        const chartConfiguration: ChartConfiguration = {
            type: 'doughnut',
            data: {
                labels: this.local.rows.map((r) => r.invoiceRowTypeLabel),
                datasets: [
                    {
                        label: `${this.$t('field.sum')}`,
                        data: this.chartData,
                        backgroundColor: this.backgroundColors,
                        borderWidth: 0,
                        hoverBorderColor: '#999999',
                        hoverBorderWidth: 2,
                    },
                ],
            },
            options: {
                ...doughnutChartOptions,
                plugins: {
                    ...doughnutChartOptions.plugins,
                    title: getTitleOptions(
                        `${this.$t('title.costStructure')} / ${this.$t('mm')}, ${this.$t(
                            'field.total',
                        )}: ${formatCurrency(this.local.sum)}`,
                    ),
                    tooltip: {
                        callbacks: {
                            label: (context: any) => {
                                let label = context.label || '';

                                if (label) {
                                    label += ': ';
                                }

                                if (context.parsed !== null && context.parsed !== undefined) {
                                    label += formatCurrency(context.parsed);
                                }

                                return label;
                            },
                        },
                    },
                },
            },
        };

        this.local.costTypeChart = new Chart(canvasContext, chartConfiguration);
    }

    private updateChart() {
        this.countTotalSum();

        if (!this.local.costTypeChart) {
            return;
        }

        if (this.local.costTypeChart.options.plugins && this.local.costTypeChart.options.plugins.title) {
            this.local.costTypeChart.options.plugins.title.text = `${this.$t('field.total')}: ${this.local.sum} €`;
        }

        if (this.local.costTypeChart.data && this.local.costTypeChart.data.datasets) {
            this.local.costTypeChart.data.datasets[0].data = this.chartData;
        }

        this.local.costTypeChart.data.labels = this.local.rows.map((r) => r.invoiceRowTypeLabel);
        this.local.costTypeChart.data.datasets[0].backgroundColor = this.backgroundColors;

        this.local.costTypeChart.update();
    }

    private countTotalSum() {
        this.local.sum = 0;
        this.local.rows.forEach((row) => {
            this.local.sum += this.showWithVat ? row.projectedIncludingVat : row.projectedExcludingVat;
        });

        this.local.sum = Math.round(this.local.sum);
    }

    private get backgroundColors(): string[] {
        const colors: string[] = [];
        this.local.rows.forEach((row) => {
            colors.push(expenseTypeColors[row.invoiceRowType]);
        });

        return colors;
    }

    private get chartData(): number[] {
        return this.showWithVat
            ? this.local.rows.map((r) => r.projectedIncludingVat)
            : this.local.rows.map((r) => r.projectedExcludingVat);
    }

    private generateFakeData(): void {
        this.local.rows = [...fakeCostReportData(this)];
    }
}
